import Err from '@leverege/error'

import { User } from './authz/index.js'
import { getId } from '../Utils.js'
/**
 * Creates a collection object that exposes list(), create() and child().
 * @param api Either an Api object or a Api.child() object
 * @param path the subpath
 **/
function collection( api, path, hasUpdate, hasDelete ) {
  
  const connect = api.child( path )

  return {
    list : connect.list,
    create : connect.post,
    search : ( obj, opts ) => {
      return connect.child( 'search' ).post( obj, opts )
    },
    get : ( id, opts ) => { return connect.child( id ).get( opts ) },
    update : hasUpdate ? ( id, patchOps, opts ) => {
      id = ( typeof id === 'string' ) ? id : id.id
      return connect.child( id ).patch( patchOps, opts )
    } : null,
    updateRaw : hasUpdate ? ( id, patchOps, opts ) => {
      id = ( typeof id === 'string' ) ? id : id.id
      return connect.child( id ).patchRaw( patchOps, opts )
    } : null,
    delete : hasDelete ? ( id, opts ) => { 
      id = ( typeof id === 'string' ) ? id : id.id
      return connect.child( id ).delete( null, opts ) 
    } : null,
    child : connect.child
  }
}

function dataObject( type, parentType, parentId, extend ) {
  parentId = parentId || `${parentType}Id`

  return ( api, obj ) => {

    let path = null
    if ( obj ) {
      if ( typeof obj === 'string' ) {
        path = `v1/${type}/${obj}`
      } else if ( obj[parentId] && obj.id ) {
        path = `v1/${parentType}/${obj[parentId]}/${type}/${obj.id}`  
      } else if ( obj.id ) {
        path = `v1/${type}/${obj.id}`
      }
    }

    if ( path == null ) {
      throw new Error( `invalid ${type}` )
    }
    const connect = api.child( path )
    const extendWith = typeof extend === 'function' ? extend( connect ) : { }
    return { get : connect.get,
             update : connect.patch,
             delete : connect.delete,
             ...extendWith }
  }
}

function authzUsers( api, path, opts ) {
  let c
  if ( opts && opts.noChild ) {
    c = api
  } else {
    c = api.child( path || 'user' )
  }

  const remove = ( body, options ) => {
    return c.delete( body, options )
  }

  return {
    list : c.list,
    add : async ( userAndRoles, options ) => {
      return c.post( userAndRoles, options )
    },

    // kinda hack-y, but a nice shortcut
    addRole( u, role /* this could default to participant */ ) {
      const id = getId( u )
      const user = new User( c.child( id ), id, { noChild : true } )

      if ( typeof role === 'object' && role != null ) {
        return user.addRole( role )
      }

      throw new Error( 'invalid role format for adding user' )
    },
    get( id, options ) {
      return c.child( typeof id === 'string' ? id : id.id, ).get( options )
    },
    remove,
    delete : remove
  }
}

function users( api, path, hasCreate, opts ) {
  let c
  if ( opts && opts.noChild ) {
    c = api
  } else {
    c = api.child( path || 'user' )
  }
  let create = null
  if ( hasCreate ) {
    create = ( user, permissions, metadata, options ) => {
      if ( arguments.length === 1 ) {
        // One object version is preferred
        const { user, permissions, metadata } = arguments[0]
        return api.child( 'createUser' ).post( { user, permissions, metadata } )
      }
      return api.child( 'createUser' ).post( { user, permissions, metadata }, options )
    }
  }

  function remove( id ) {
    return c.child( typeof id === 'string' ? id : id.id, ).delete( )
  }

  return {
    list : c.list,
    create,
    add( id, permissions, metadata ) {
      if ( id != null && arguments.length === 1 && typeof id === 'object' ) {
        // One object version is prefered
        let opts
        const { permissions, metadata, id, user, username } = arguments[0]
        if ( permissions == null ) {
          throw Err.badRequest( 'permissions is not valid' )
        }
        
        if ( id ) {
          opts = { id, permissions, metadata }
        } else if ( username ) {
          opts = { username, permissions, metadata }
        } else if ( user && typeof user.id === 'string' ) {
          opts = { id : user.id, permissions, metadata }
        } else {
          throw Err.badRequest( 'id, username, or a valid user is not given' )
        }
        return c.post( opts )
      }

      // allow either a (id, perm, meta) or a ( id, { permission, metadata })
      if ( permissions && permissions.permissions != null ) {
        metadata = permissions.metadata || null
        permissions = permissions.permissions
      } else { 
        permissions = permissions == null ? null : permissions
        metadata = metadata == null ? null : metadata
      }
      id = typeof id === 'string' ? id : id.id
      return c.post( { id, permissions, metadata } )
    },

    get( id, options ) {
      return c.child( typeof id === 'string' ? id : id.id, ).get( options )
    },

    update( id, ops ) {
      id = typeof id === 'string' ? id : id.id
      return c.child( id ).patch( ops )
    },

    remove,
    delete : remove
  }
}

export default {
  collection,
  users,
  authzUsers,
  dataObject
}
